import { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../../../../services/firebase";
import { TenantContext } from "../../../../contexts/TenantContext";
import { DEFAULT_WEIGHTS } from "../../../../constants/performance";
import { toast } from "react-toastify";

const GROUPS = ["professor", "consultor", "gerente"];

/**
 * useGoals — Hook para CRUD de metas de performance por grupo de cargo e por mês.
 *
 * Estrutura Firestore:
 *   tenants/{idTenant}/performanceGoals/professor_2026-04  → metas de Abril/2026
 *   tenants/{idTenant}/performanceGoals/professor           → metas padrão (fallback)
 *
 * Fluxo de leitura:
 *   1. Tenta carregar o doc com sufixo do mês (ex: professor_2026-04)
 *   2. Se não existir, carrega o doc base (ex: professor)
 *   3. Se nenhum existir, usa DEFAULT_WEIGHTS com goals zerados
 */
const useGoals = () => {
    const { user, idTenant } = useContext(TenantContext);

    const [goalsData, setGoalsData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Mês selecionado (YYYY-MM)
    const now = new Date();
    const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);

    // Gerar lista de meses (últimos 3 + atual + próximos 3)
    const periodOptions = useMemo(() => {
        const options = [];
        const baseDate = new Date();
        for (let i = -3; i <= 3; i++) {
            const d = new Date(baseDate.getFullYear(), baseDate.getMonth() + i, 1);
            const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
                "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
            const label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
            options.push({ value, label });
        }
        return options;
    }, []);

    // ── Construir fallback com DEFAULT_WEIGHTS ──
    const buildDefault = (group) => {
        const defaultWeights = DEFAULT_WEIGHTS[group] || {};
        const emptyGoals = {};
        Object.keys(defaultWeights).forEach(key => {
            emptyGoals[key] = 0;
        });
        return {
            metricGroup: group,
            goals: emptyGoals,
            weights: { ...defaultWeights },
            isDefault: true,
        };
    };

    // ── Carregar dados de todos os grupos para o período selecionado ──
    useEffect(() => {
        if (!idTenant) {
            setLoading(false);
            return;
        }

        const loadGoals = async () => {
            setLoading(true);
            try {
                const data = {};

                // Carregar os 3 grupos em paralelo (3 reads simultâneos ao invés de sequenciais)
                const results = await Promise.all(GROUPS.map(async (group) => {
                    // 1) Tentar doc mensal (ex: professor_2026-04)
                    const monthDocId = `${group}_${selectedPeriod}`;
                    const monthRef = doc(db, "tenants", idTenant, "performanceGoals", monthDocId);
                    const monthSnap = await getDoc(monthRef);

                    if (monthSnap.exists()) {
                        return { group, data: { ...monthSnap.data(), isDefault: false } };
                    }

                    // 2) Tentar doc base (ex: professor)
                    const baseRef = doc(db, "tenants", idTenant, "performanceGoals", group);
                    const baseSnap = await getDoc(baseRef);

                    if (baseSnap.exists()) {
                        return { group, data: { ...baseSnap.data(), isDefault: true, isFromBase: true } };
                    }

                    // 3) Fallback com DEFAULT_WEIGHTS
                    return { group, data: buildDefault(group) };
                }));

                results.forEach(({ group, data: groupData }) => {
                    data[group] = groupData;
                });
                setGoalsData(data);
            } catch (err) {
                console.error("[useGoals] Erro ao carregar metas:", err);
                // Fallback total em caso de erro
                const fallback = {};
                GROUPS.forEach(g => { fallback[g] = buildDefault(g); });
                setGoalsData(fallback);
            } finally {
                setLoading(false);
            }
        };

        loadGoals();
    }, [idTenant, selectedPeriod]);

    // ── Salvar metas e pesos de um grupo para o mês selecionado ──
    const handleSave = useCallback(async (metricGroup, goals, weights) => {
        if (!idTenant || !metricGroup) return;
        setSaving(true);
        try {
            // Salvar como doc mensal (ex: professor_2026-04)
            const monthDocId = `${metricGroup}_${selectedPeriod}`;
            const docRef = doc(db, "tenants", idTenant, "performanceGoals", monthDocId);
            const payload = {
                metricGroup,
                period: selectedPeriod,
                goals: goals || {},
                weights: weights || {},
                updatedAt: Timestamp.now(),
                updatedBy: {
                    uid: user?.uid || "system",
                    name: user?.fullName || user?.displayName || user?.email || "Sistema",
                },
            };
            await setDoc(docRef, payload, { merge: true });

            // Atualizar state local
            setGoalsData(prev => ({
                ...prev,
                [metricGroup]: { ...prev[metricGroup], ...payload, isDefault: false },
            }));

            toast.success(`Metas salvas para ${selectedPeriod}!`);
        } catch (err) {
            console.error("[useGoals] Erro ao salvar metas:", err);
            toast.error("Erro ao salvar metas.");
            throw err;
        } finally {
            setSaving(false);
        }
    }, [idTenant, user, selectedPeriod]);

    // ── Aplicar um template pré-configurado ──
    const applyTemplate = useCallback((templateGroups) => {
        if (!templateGroups) return;
        const newData = {};
        GROUPS.forEach(group => {
            const tmpl = templateGroups[group];
            if (tmpl) {
                newData[group] = {
                    metricGroup: group,
                    goals: { ...tmpl.goals },
                    weights: { ...tmpl.weights },
                    isDefault: false,
                    isTemplate: true,
                };
            } else {
                newData[group] = goalsData[group] || buildDefault(group);
            }
        });
        setGoalsData(newData);
        toast.info("Template aplicado! Revise os valores e clique em Salvar.");
    }, [goalsData]);

    // ── Aplicar e Salvar Template no Ano Inteiro ──
    const applyTemplateYearly = useCallback(async (templateGroups) => {
        if (!idTenant || !templateGroups) return;
        setSaving(true);
        try {
            const currentYear = selectedPeriod.split('-')[0];
            const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
            
            const batchPromises = [];
            months.forEach(month => {
                const docPeriod = `${currentYear}-${month}`;
                GROUPS.forEach(group => {
                    const tmpl = templateGroups[group];
                    if (tmpl) {
                        const docId = `${group}_${docPeriod}`;
                        const docRef = doc(db, "tenants", idTenant, "performanceGoals", docId);
                        const payload = {
                            metricGroup: group,
                            period: docPeriod,
                            goals: tmpl.goals || {},
                            weights: tmpl.weights || {},
                            updatedAt: Timestamp.now(),
                            updatedBy: {
                                uid: user?.uid || "system",
                                name: user?.fullName || user?.displayName || user?.email || "Sistema",
                            },
                        };
                        batchPromises.push(setDoc(docRef, payload, { merge: true }));
                    }
                });
            });

            await Promise.all(batchPromises);
            
            // Apply to current UI state to reflect changes
            applyTemplate(templateGroups);
            // Replace toast for yearly
            toast.success(`Template salvo e replicado para todo o ano de ${currentYear}!`);
        } catch (err) {
            console.error("[useGoals] Erro ao replicar template no ano:", err);
            toast.error("Erro ao aplicar template anual.");
        } finally {
            setSaving(false);
        }
    }, [idTenant, user, selectedPeriod, applyTemplate]);

    return {
        goalsData,
        loading,
        saving,
        selectedPeriod,
        setSelectedPeriod,
        periodOptions,
        handleSave,
        applyTemplate,
        applyTemplateYearly,
    };
};

export default useGoals;
